import type {
  Completion,
  TreasureLoadout,
  TreasurePrefs,
  TreasureRewardId,
  TreasureSlot,
} from "@/lib/schemas";

export const TREASURE_START_DAY = "2026-08-06";
export const TREASURE_KEYS_PER_CHEST = 3;

export type TreasureReward = {
  id: TreasureRewardId;
  slot: TreasureSlot;
  title: string;
  description: string;
  asset:
    | "fox"
    | "cat"
    | "dog"
    | "chicken"
    | "tiger"
    | "backpack"
    | "school-backpack"
    | "weekend-backpack"
    | "hiking-backpack"
    | "travel-backpack"
    | "glasses"
    | "sunglasses"
    | "red-sunglasses"
    | "mirror-glasses"
    | "cat-eye-glasses"
    | "dollar-glasses"
    | "hoodie"
    | "chain"
    | "hair"
    | "aura"
    | "scene";
  variant:
    | "original"
    | "sunny"
    | "berry"
    | "night"
    | "sky"
    | "meadow"
    | "red"
    | "black"
    | "orange"
    | "mirror";
  tone: "hay" | "green" | "purple" | "sky";
  scenePath?: string;
};

export const TREASURE_REWARDS: readonly TreasureReward[] = [
  {
    id: "fox-companion",
    slot: "companion",
    title: "Lisek wędrowiec",
    description: "Czeka obok bohatera na każdą nową misję.",
    asset: "fox",
    variant: "original",
    tone: "hay",
  },
  {
    id: "cat-companion",
    slot: "companion",
    title: "Kotek ciekawski",
    description: "Zagląda do każdej skrzynki razem z bohaterem.",
    asset: "cat",
    variant: "original",
    tone: "hay",
  },
  {
    id: "dog-companion",
    slot: "companion",
    title: "Piesek kompan",
    description: "Najwierniejszy partner rodzinnych misji.",
    asset: "dog",
    variant: "sunny",
    tone: "hay",
  },
  {
    id: "chicken-companion",
    slot: "companion",
    title: "Kura odkrywczyni",
    description: "Dziobie tropy do kolejnych zadań.",
    asset: "chicken",
    variant: "original",
    tone: "hay",
  },
  {
    id: "tiger-companion",
    slot: "companion",
    title: "Tygrys odważniak",
    description: "Mały tygrys do naprawdę wielkich misji.",
    asset: "tiger",
    variant: "sunny",
    tone: "hay",
  },
  {
    id: "explorer-backpack",
    slot: "backpack",
    title: "Mały plecak jednodniowy",
    description: "Lekki plecak na krótką misję.",
    asset: "backpack",
    variant: "original",
    tone: "green",
  },
  {
    id: "school-backpack",
    slot: "backpack",
    title: "Plecak szkolny",
    description: "Kieszeń na notes, bidon i ważne drobiazgi.",
    asset: "school-backpack",
    variant: "original",
    tone: "green",
  },
  {
    id: "weekend-backpack",
    slot: "backpack",
    title: "Plecak weekendowy",
    description: "Więcej kieszeni i koc na dłuższą wyprawę.",
    asset: "weekend-backpack",
    variant: "original",
    tone: "sky",
  },
  {
    id: "hiking-backpack",
    slot: "backpack",
    title: "Plecak górski",
    description: "Duży plecak z matą i pasami na szlak.",
    asset: "hiking-backpack",
    variant: "original",
    tone: "green",
  },
  {
    id: "travel-backpack",
    slot: "backpack",
    title: "Plecak podróżnika",
    description: "Największy ekwipunek na wielką ekspedycję.",
    asset: "travel-backpack",
    variant: "original",
    tone: "hay",
  },
  {
    id: "round-glasses",
    slot: "glasses",
    title: "Okulary tropiciela",
    description: "Pomagają wypatrzyć zadania do zrobienia.",
    asset: "glasses",
    variant: "original",
    tone: "green",
  },
  {
    id: "dark-sunglasses",
    slot: "glasses",
    title: "Czarne okulary",
    description: "Ciemne szkła do zadań w tajnym stylu.",
    asset: "sunglasses",
    variant: "black",
    tone: "purple",
  },
  {
    id: "red-sunglasses",
    slot: "glasses",
    title: "Czerwone okulary",
    description: "Mocne czerwone oprawki i ciemne szkła.",
    asset: "red-sunglasses",
    variant: "red",
    tone: "hay",
  },
  {
    id: "mirror-glasses",
    slot: "glasses",
    title: "Lustrzane okulary",
    description: "Kosmiczna niebieska szyba odbija całe niebo.",
    asset: "mirror-glasses",
    variant: "mirror",
    tone: "sky",
  },
  {
    id: "cat-eye-glasses",
    slot: "glasses",
    title: "Kocie okulary",
    description: "Spiczaste oprawki z odrobiną złota.",
    asset: "cat-eye-glasses",
    variant: "black",
    tone: "purple",
  },
  {
    id: "dollar-glasses",
    slot: "glasses",
    title: "Okulary dolary",
    description: "Złote oprawki w kształcie dwóch znaków dolara.",
    asset: "dollar-glasses",
    variant: "sunny",
    tone: "hay",
  },
  {
    id: "sky-hoodie",
    slot: "hoodie",
    title: "Bluza nieba",
    description: "Błękitna bluza na lekkie misje.",
    asset: "hoodie",
    variant: "sky",
    tone: "sky",
  },
  {
    id: "red-hoodie",
    slot: "hoodie",
    title: "Czerwona bluza",
    description: "Energetyczna czerwień na odważny dzień.",
    asset: "hoodie",
    variant: "red",
    tone: "hay",
  },
  {
    id: "black-hoodie",
    slot: "hoodie",
    title: "Czarna bluza",
    description: "Klasyczny ciemny styl bohatera.",
    asset: "hoodie",
    variant: "black",
    tone: "purple",
  },
  {
    id: "berry-hoodie",
    slot: "hoodie",
    title: "Bluza jagodowa",
    description: "Fioletowa bluza dla mistrzów Polany.",
    asset: "hoodie",
    variant: "berry",
    tone: "purple",
  },
  {
    id: "orange-hoodie",
    slot: "hoodie",
    title: "Pomarańczowa bluza",
    description: "Ciepły kolor zachodzącego słońca.",
    asset: "hoodie",
    variant: "orange",
    tone: "hay",
  },
  {
    id: "gold-dollar-chain",
    slot: "necklace",
    title: "Złoty łańcuch",
    description: "Gruby łańcuch z dolarowym medalionem.",
    asset: "chain",
    variant: "sunny",
    tone: "hay",
  },
  {
    id: "silver-star-chain",
    slot: "necklace",
    title: "Srebrny łańcuch",
    description: "Chłodny blask dla odkrywcy nocnego nieba.",
    asset: "chain",
    variant: "sky",
    tone: "sky",
  },
  {
    id: "clover-chain",
    slot: "necklace",
    title: "Łańcuch koniczyny",
    description: "Zielony amulet szczęścia wypracowanego samemu.",
    asset: "chain",
    variant: "meadow",
    tone: "green",
  },
  {
    id: "blue-mohawk",
    slot: "hair",
    title: "Niebieski irokez",
    description: "Odważna fryzura prawdziwego odkrywcy.",
    asset: "hair",
    variant: "original",
    tone: "sky",
  },
  {
    id: "berry-mohawk",
    slot: "hair",
    title: "Jagodowy irokez",
    description: "Fioletowa fryzura pełna charakteru.",
    asset: "hair",
    variant: "berry",
    tone: "purple",
  },
  {
    id: "sunny-mohawk",
    slot: "hair",
    title: "Słoneczny irokez",
    description: "Złota fryzura dla mistrza Polany.",
    asset: "hair",
    variant: "sunny",
    tone: "hay",
  },
  {
    id: "leaf-aura",
    slot: "aura",
    title: "Wirujące listki",
    description: "Zielone listki krążą wokół bohatera.",
    asset: "aura",
    variant: "meadow",
    tone: "green",
  },
  {
    id: "star-aura",
    slot: "aura",
    title: "Gwiezdna aura",
    description: "Spokojne iskry nocnego odkrywcy.",
    asset: "aura",
    variant: "night",
    tone: "purple",
  },
  {
    id: "rainbow-aura",
    slot: "aura",
    title: "Tęczowy ślad",
    description: "Kolorowa smuga dobrej roboty.",
    asset: "aura",
    variant: "sky",
    tone: "sky",
  },
  {
    id: "meadow-scene",
    slot: "scene",
    title: "Polana Sianka",
    description: "Słoneczna polana, od której zaczyna się przygoda.",
    asset: "scene",
    variant: "meadow",
    tone: "green",
    scenePath: "/treasure/meadow.webp?v=1",
  },
  {
    id: "cozy-room-scene",
    slot: "scene",
    title: "Przytulny pokój",
    description: "Ciepły pokój na poddaszu z okrągłym oknem.",
    asset: "scene",
    variant: "sunny",
    tone: "hay",
    scenePath: "/treasure/scenes/cozy-room.webp?v=1",
  },
  {
    id: "treehouse-room-scene",
    slot: "scene",
    title: "Baza w domku na drzewie",
    description: "Leśna pracownia pełna map i odkryć.",
    asset: "scene",
    variant: "meadow",
    tone: "green",
    scenePath: "/treasure/scenes/treehouse-room.webp?v=1",
  },
  {
    id: "space-room-scene",
    slot: "scene",
    title: "Kosmiczne obserwatorium",
    description: "Pokój z teleskopem i wielką planetą za oknem.",
    asset: "scene",
    variant: "night",
    tone: "purple",
    scenePath: "/treasure/scenes/space-room.webp?v=1",
  },
  {
    id: "club-room-scene",
    slot: "scene",
    title: "Pokój dobrej zabawy",
    description: "Klubowa baza z grami, pufami i trofeami.",
    asset: "scene",
    variant: "orange",
    tone: "hay",
    scenePath: "/treasure/scenes/club-room.webp?v=1",
  },
] as const;

export const EMPTY_TREASURE_PREFS: TreasurePrefs = {
  openedChestCount: 0,
  ownedRewardIds: [],
};

export type TreasureProgress = {
  totalKeys: number;
  earnedChestCount: number;
  openedChestCount: number;
  availableChestCount: number;
  keysTowardNextChest: number;
  keysNeededForNextChest: number;
  activeDayKeys: string[];
};

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (
    value &&
    typeof (value as { toDate?: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function normalizeTreasurePrefs(
  prefs: TreasurePrefs | undefined,
): TreasurePrefs {
  if (!prefs) return EMPTY_TREASURE_PREFS;
  const uniqueOwned = [...new Set(prefs.ownedRewardIds)];
  const equipped = Object.fromEntries(
    Object.entries(prefs.equipped ?? {}).filter(([slot, rewardId]) => {
      if (!uniqueOwned.includes(rewardId)) return false;
      return treasureRewardById(rewardId)?.slot === slot;
    }),
  ) as TreasureLoadout;
  return {
    openedChestCount: Math.max(0, prefs.openedChestCount),
    ownedRewardIds: uniqueOwned,
    ...(Object.keys(equipped).length > 0 ? { equipped } : {}),
  };
}

export function computeTreasureProgress(
  approved: readonly Completion[],
  openedChestCount: number,
): TreasureProgress {
  const activeDays = new Set<string>();
  for (const completion of approved) {
    if (completion.status !== "approved") continue;
    if (!completion.proofPhotoUrl) continue;
    const date = toDate(completion.approvedAt ?? completion.completedAt);
    if (!date) continue;
    const key = localDayKey(date);
    if (key >= TREASURE_START_DAY) activeDays.add(key);
  }

  const activeDayKeys = [...activeDays].sort();
  const totalKeys = activeDayKeys.length;
  const earnedChestCount = Math.floor(totalKeys / TREASURE_KEYS_PER_CHEST);
  const safeOpenedCount = Math.max(0, Math.floor(openedChestCount));
  const keysTowardNextChest = totalKeys % TREASURE_KEYS_PER_CHEST;

  return {
    totalKeys,
    earnedChestCount,
    openedChestCount: safeOpenedCount,
    availableChestCount: Math.max(0, earnedChestCount - safeOpenedCount),
    keysTowardNextChest,
    keysNeededForNextChest: TREASURE_KEYS_PER_CHEST - keysTowardNextChest,
    activeDayKeys,
  };
}

export function drawRandomTreasureReward(
  ownedRewardIds: readonly TreasureRewardId[],
  randomValue?: number,
): TreasureReward | undefined {
  const owned = new Set(ownedRewardIds);
  const available = TREASURE_REWARDS.filter((reward) => !owned.has(reward.id));
  if (available.length === 0) return undefined;

  const unit = randomValue ?? secureRandomUnit();
  const index = Math.min(
    available.length - 1,
    Math.floor(Math.max(0, unit) * available.length),
  );
  return available[index];
}

export function treasureRewardById(
  id: TreasureRewardId | undefined,
): TreasureReward | undefined {
  return id ? TREASURE_REWARDS.find((reward) => reward.id === id) : undefined;
}

export function treasureRewardsForSlot(slot: TreasureSlot): TreasureReward[] {
  return TREASURE_REWARDS.filter((reward) => reward.slot === slot);
}

export function treasureMeadowStage(ownedRewardCount: number): 0 | 1 | 2 | 3 {
  if (ownedRewardCount >= 30) return 3;
  if (ownedRewardCount >= 20) return 2;
  if (ownedRewardCount >= 10) return 1;
  return 0;
}

export function treasureCompanionMessage(
  companionId: TreasureRewardId | undefined,
  done: number,
  total: number,
  fallback: string,
): string {
  if (!companionId || total === 0) return fallback;
  const reward = treasureRewardById(companionId);
  if (reward?.slot !== "companion") return fallback;

  const phase = done >= total ? "done" : done > 0 ? "progress" : "start";
  const messages: Record<
    | "fox-companion"
    | "cat-companion"
    | "dog-companion"
    | "chicken-companion"
    | "tiger-companion",
    Record<typeof phase, string>
  > = {
    "fox-companion": {
      start: "Lisek już tropi pierwszą misję!",
      progress: "Lisek mówi: dobry trop, idziemy dalej!",
      done: "Lisek świętuje razem z Tobą!",
    },
    "cat-companion": {
      start: "Kotek już zagląda do pierwszej misji!",
      progress: "Kotek mruczy: świetnie Ci idzie!",
      done: "Kotek mruczy z dumy nad gotową listą!",
    },
    "dog-companion": {
      start: "Piesek już merda na pierwszą misję!",
      progress: "Piesek biegnie z Tobą dalej!",
      done: "Piesek skacze z radości — wszystko gotowe!",
    },
    "chicken-companion": {
      start: "Kura wydziobała trop do pierwszej misji!",
      progress: "Kura gdacze: tak trzymać!",
      done: "Kura ogłasza koniec zadań na dziś!",
    },
    "tiger-companion": {
      start: "Tygrys rusza odważnie na pierwszą misję!",
      progress: "Tygrys mówi: jeszcze kawałek szlaku!",
      done: "Tygrys ryczy z dumy — lista gotowa!",
    },
  };
  return messages[reward.id as keyof typeof messages][phase];
}

function secureRandomUnit(): number {
  if (globalThis.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0] / 4_294_967_296;
  }
  return Math.random();
}
